import type { JSX } from 'react'
import type { ITabsProps } from 'blocks/Tabs'
import type { COLOR } from 'vars/color'
import type { INDENTS } from 'vars/index'

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
  FluidControlsTemplate,
  InlineRowControlsTemplate,
} from 'blocks/Container/Controls/Templates'
import { Tabs } from 'blocks/Tabs'
import { useClientWidth, useTablet } from 'utils/hooks'
import { FIXED_INDENTS } from 'vars/index'
import { useTabsScroll } from './hooks/useTabsScroll'
import { TabControlsType, TabsPaddings } from './TabsContainer.h'
import { TabsContainerStyled } from './TabsContainer.styled'

export type ITabsContainerProps = Omit<
  ITabsProps,
  'title' | 'nodeRef' | 'paddings'
> & {
  gap?: FIXED_INDENTS | INDENTS
  controlsType?: TabControlsType
  paddings?: (typeof TabsPaddings)[keyof typeof TabsPaddings]
  itemCount?: number
  onLoadMoreItems?: () => void
  isLast?: boolean
  groupCell?: boolean
  backgroundColor?: COLOR
}

const PERCENT_TO_LOADING = 0.7

export const TabsContainer = ({
  gap = FIXED_INDENTS.INDENT_80,
  controlsType = TabControlsType.inRow,
  paddings = TabsPaddings.DEFAULT_70,
  children,
  itemCount,
  onLoadMoreItems,
  isLast,
  groupCell = true,
  backgroundColor,
}: ITabsContainerProps): JSX.Element => {
  const [leftButtonVisible, setLeftButtonVisible] = useState<boolean>(false)
  const [rightButtonVisible, setRightButtonVisible] = useState<boolean>(true)
  const [activeTab, setActiveTab] = useState<number>(0)
  const [scrollLeft, setScrollLeft] = useState<number | undefined>()
  const [isScrolling, setIsScrolling] = useState(false)
  const [count, setCount] = useState(0)
  const clientWidth = useClientWidth()
  const isTablet = useTablet()

  const tabsRef = useRef<HTMLDivElement>(null)

  const { handleScroll, prev, next } = useTabsScroll({
    tabsRef,
    activeTab,
    leftButtonVisible,
    setCount,
    setIsScrolling,
    setLeftButtonVisible,
    setRightButtonVisible,
    setScrollLeft,
    itemCount,
    rightButtonVisible,
    clientWidth,
    groupCell,
  })

  useEffect(() => {
    if (tabsRef.current) {
      const { scrollWidth } = tabsRef.current
      const width = scrollWidth > scrollWidth * PERCENT_TO_LOADING
      if (!isLast && onLoadMoreItems && width) onLoadMoreItems()
    }
  }, [scrollLeft])

  useEffect(() => {
    if (!leftButtonVisible && !rightButtonVisible) setActiveTab(0)
  }, [leftButtonVisible, rightButtonVisible])

  const activeItem = useMemo(() => {
    if (tabsRef.current) {
      const maxSkipIndex = 1
      const array = Array.from(tabsRef.current.children) as HTMLDivElement[]
      const firstChild = tabsRef.current.firstChild as HTMLDivElement
      const indexItem = array.findIndex(
        ({ offsetLeft }) => offsetLeft >= scrollLeft! + firstChild.offsetLeft
      )
      return indexItem <= maxSkipIndex ? 0 : Math.max(0, indexItem)
    }
    return 0
  }, [scrollLeft, itemCount])

  useLayoutEffect(() => {
    const ref = tabsRef.current

    if (ref && ref.clientWidth === ref.scrollWidth) {
      setLeftButtonVisible(false)
      setRightButtonVisible(false)
    }
    if (ref) {
      const { scrollWidth, clientWidth } = ref
      const width = scrollWidth - clientWidth !== Math.round(scrollLeft!)
      if (scrollWidth === clientWidth) {
        return setLeftButtonVisible(false)
      }

      setRightButtonVisible(width)
      setActiveTab(activeItem)
    }
    setLeftButtonVisible(activeItem !== 0)
  }, [activeTab, scrollLeft, clientWidth, count, itemCount])

  const Controls = () => {
    const controlsState = [
      {
        visible: leftButtonVisible,
        disabled: false,
        onClick: prev,
      },
      {
        visible: rightButtonVisible,
        disabled: false,
        onClick: next,
      },
    ]
    switch (controlsType) {
      case TabControlsType.inRow:
        return <InlineRowControlsTemplate controlsState={controlsState} />
      case TabControlsType.inRowSecondary:
        return <FluidControlsTemplate controlsState={controlsState} />
      default:
        return <InlineRowControlsTemplate controlsState={controlsState} />
    }
  }

  return (
    <TabsContainerStyled
      paddings={paddings}
      controlsType={controlsType}
      isTablet={isTablet}
      isScrolling={isScrolling}
      backgroundColor={backgroundColor}
    >
      {!isTablet && <Controls />}
      <Tabs gap={gap} nodeRef={tabsRef} handleScroll={handleScroll}>
        {children}
      </Tabs>
    </TabsContainerStyled>
  )
}
